import React, { useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useSport } from '../contexts/SportContext';

const CUSTOM_SPORT_KEY = 'gonkle_custom_sport';
const highlightSportKeys = [
    'football',
    'basketball',
    'volleyball',
    'tabletennis',
    'frisbee',
    'running',
    'cycling'
];

const ChangeVibeModal = ({ onClose, onSave }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { sportTypes } = useSport();
    const customAddLabel = t('event_custom_add_label');
    const customEditLabel = t('event_custom_edit_label');
    const [selectedSport, setSelectedSport] = useState(user?.favorite_sport || 'football');
    const [showSportCatalog, setShowSportCatalog] = useState(false);
    const [customSportKey, setCustomSportKey] = useState(() => {
        try {
            return localStorage.getItem(CUSTOM_SPORT_KEY) || '';
        } catch (error) {
            return '';
        }
    });

    const highlightSports = useMemo(
        () => highlightSportKeys.map(key => sportTypes?.[key]).filter(Boolean),
        [sportTypes]
    );

    const catalogSports = useMemo(
        () => Object.values(sportTypes || {}).sort((a, b) => a.name.localeCompare(b.name)),
        [sportTypes]
    );

    const customSport = customSportKey ? sportTypes?.[customSportKey] : null;
    const gridItems = [...highlightSports, { key: 'custom', isCustom: true }];

    const handleCustomSportSelect = (sportKey) => {
        setCustomSportKey(sportKey);
        setSelectedSport(sportKey);
        try {
            localStorage.setItem(CUSTOM_SPORT_KEY, sportKey);
        } catch (error) {
            // Ignore storage errors
        }
        setShowSportCatalog(false);
    };

    const handleSportSelect = (sportKey) => {
        // Direct save on sport selection (no separate OK button)
        onSave(sportKey);
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <motion.div
                className="modal-content"
                onClick={e => e.stopPropagation()}
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 100, damping: 20 }}
                style={{ position: 'relative' }}
            >
                <h2 style={{marginTop: 0, textAlign: 'center', fontFamily: 'var(--font-display)'}}>{t('change_favorite_gonkle_button')}</h2>

                <div style={{ width: '100%', marginTop: '12px', marginBottom: '20px' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                        gap: 10,
                    }}>
                        {gridItems.map(item => {
                            if (item.isCustom) {
                                const isSelected = customSport?.key === selectedSport;
                                return (
                                    <button
                                        key="grid-custom"
                                        type="button"
                                        onClick={() => {
                                            if (customSport) {
                                                handleSportSelect(customSport.key);
                                            } else {
                                                setShowSportCatalog(true);
                                            }
                                        }}
                                        style={{
                                            borderRadius: 14,
                                            border: `2px dashed ${isSelected ? 'var(--color-accent)' : 'rgba(15, 23, 42, 0.2)'}`,
                                            backgroundColor: '#f4f6ff',
                                            color: '#0f172a',
                                            padding: '12px 8px',
                                            minHeight: 86,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 6,
                                            fontFamily: 'var(--font-display)',
                                            position: 'relative',
                                            boxShadow: isSelected ? '0 15px 30px rgba(15, 23, 42, 0.25)' : '0 6px 12px rgba(15, 23, 42, 0.08)'
                                        }}
                                    >
                                        {customSport && (
                                            <div
                                                role="button"
                                                tabIndex="0"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setShowSportCatalog(true);
                                                }}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.stopPropagation();
                                                        setShowSportCatalog(true);
                                                    }
                                                }}
                                                aria-label={customEditLabel}
                                                style={{
                                                    position: 'absolute',
                                                    top: 6,
                                                    right: 6,
                                                    border: '1px solid rgba(15, 23, 42, 0.15)',
                                                    background: 'rgba(248, 250, 255, 0.9)',
                                                    color: '#0f172a',
                                                    borderRadius: 6,
                                                    padding: '2px 6px',
                                                    fontSize: 10,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                ✎
                                            </div>
                                        )}
                                        <span style={{ fontSize: 30 }}>{customSport ? customSport.icon : '➕'}</span>
                                        <span style={{ fontSize: 11, fontWeight: 600 }}>{customSport ? customSport.name : customAddLabel}</span>
                                    </button>
                                );
                            }
                            const isSelected = item.key === selectedSport;
                            return (
                                <button
                                    key={item.key}
                                    type="button"
                                    onClick={() => handleSportSelect(item.key)}
                                    style={{
                                        borderRadius: 14,
                                        border: `2px solid ${isSelected ? 'var(--color-accent)' : 'rgba(15, 23, 42, 0.15)'}`,
                                        backgroundColor: isSelected ? '#eef2ff' : '#ffffff',
                                        color: '#0f172a',
                                        padding: '12px 8px',
                                        minHeight: 86,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 6,
                                        fontFamily: 'var(--font-display)',
                                        boxShadow: isSelected ? '0 15px 30px rgba(15, 23, 42, 0.2)' : '0 6px 12px rgba(15, 23, 42, 0.08)'
                                    }}
                                >
                                    <span style={{ fontSize: 30 }}>{item.icon}</span>
                                    <span style={{ fontSize: 11, fontWeight: 600 }}>{item.name}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <AnimatePresence>
                    {showSportCatalog && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.96 }}
                            transition={{ type: 'spring', stiffness: 200, damping: 25 }}
                            style={{
                                position: 'absolute',
                                inset: 0,
                                zIndex: 5,
                                backgroundColor: 'rgba(248, 250, 255, 0.98)',
                                borderRadius: 'var(--border-radius-large)',
                                padding: '1.25rem',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 12,
                                boxShadow: '0 25px 45px rgba(15, 23, 42, 0.25)',
                                border: '1px solid rgba(15, 23, 42, 0.08)'
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{t('all_sports_button', 'All Gonkles')}</p>
                                <button
                                    type="button"
                                    onClick={() => setShowSportCatalog(false)}
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        color: 'var(--color-text-primary)',
                                        fontSize: 20,
                                        cursor: 'pointer',
                                        padding: 0
                                    }}
                                    aria-label={t('close_button', 'Close')}
                                >
                                    ×
                                </button>
                            </div>
                            <div style={{ flex: 1, overflowY: 'auto' }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(84px, 1fr))',
                                    gap: 10
                                }}>
                                    {catalogSports.map(sport => (
                                        <button
                                            key={sport.key}
                                            type="button"
                                            onClick={() => handleCustomSportSelect(sport.key)}
                                            style={{
                                                borderRadius: 12,
                                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                                color: 'var(--color-text-primary)',
                                                padding: '10px',
                                                minHeight: 82,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                gap: 4,
                                                fontSize: 10
                                            }}
                                        >
                                            <span style={{ fontSize: 30 }}>{sport.icon}</span>
                                            <span style={{ fontSize: 11, fontWeight: 600 }}>{sport.name}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
};

export default ChangeVibeModal;
